// --- START EDIT: Pass content selector, add data-masonry markers, keep a11y, and ensure spans apply ---
import { useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import { Badge } from "@/lib/components/ui/badge";
import { Card } from "@/lib/components/ui/card";
import * as Dialog from "@radix-ui/react-dialog";
import { setMetaDescription, setPageTitle } from "@/lib/seo";
import { usePortfolioProjects } from "@/data/usePortfolioProjects";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useMasonryGrid } from "@/lib/useMasonryGrid";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

type LightboxState = {
  open: boolean;
  projectIndex: number;
  imageIndex: number;
};

const Portfolio = () => {
  const { t, i18n } = useTranslation("marketing");
  const portfolioProjects = usePortfolioProjects();

  useEffect(() => {
    setPageTitle(t("portfolio.title") + " • 3D Printing");
    setMetaDescription(t("portfolio.description"));
  }, [t]);

  const reduced = usePrefersReducedMotion();

  const GRID_ROW_HEIGHT = 8;

  // Tell the hook what to measure inside each item
  const {
    gridRef,
    setItemRef,
    getRowSpan,
    recalculate,
    handleImageLoad,
  } = useMasonryGrid({
    rowHeight: GRID_ROW_HEIGHT,
    debounceDelay: 100,
    contentSelector: "[data-masonry-content]",
  });

  const cards = useMemo(
    () =>
      portfolioProjects.map((p) => ({
        slug: p.slug,
        title: p.title,
        summary: p.summary,
        category: p.category,
        tags: p.tags ?? [],
        coverIndex: p.coverIndex ?? 0,
        cover: p.images[p.coverIndex ?? 0],
        images: p.images,
      })),
    [portfolioProjects]
  );

  useEffect(() => {
    recalculate();
  }, [i18n.language, recalculate]);

  const [lb, setLb] = useState<LightboxState>({
    open: false,
    projectIndex: 0,
    imageIndex: 0,
  });

  const goto = useCallback(
    (dir: -1 | 1) =>
      setLb((s) => {
        const images = cards[s.projectIndex].images;
        const next = (s.imageIndex + dir + images.length) % images.length;
        return { ...s, imageIndex: next };
      }),
    [cards]
  );

  const openLightbox = (projectIndex: number, imageIndex = 0) =>
    setLb({ open: true, projectIndex, imageIndex });

  const closeLightbox = () => setLb((s) => ({ ...s, open: false }));

  useEffect(() => {
    if (!lb.open) return;
    const images = cards[lb.projectIndex]?.images || [];
    const next = images[(lb.imageIndex + 1) % images.length]?.src;
    const prev = images[(lb.imageIndex - 1 + images.length) % images.length]?.src;
    [next, prev].forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
  }, [lb, cards]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!lb.open) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goto(1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goto(-1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lb.open, goto]);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
      {/* Hero */}
      <section className="px-6 pt-12 pb-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            {t("portfolio.title")}
          </h1>
        <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            {t("portfolio.description")}
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          <div
            ref={gridRef}
            className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            style={{ gridAutoRows: `${GRID_ROW_HEIGHT}px` }}
          >
            {cards.map((c, idx) => {
              const count = c.images.length;
              const rowSpan = getRowSpan(c.slug);
              return (
                <Card
                  key={c.slug}
                  ref={setItemRef(c.slug)}
                  data-masonry-item=""
                  className="overflow-hidden bg-card/90 backdrop-blur border-border shadow-sm hover:shadow-md transition-shadow p-0"
                  style={{ gridRowEnd: `span ${rowSpan}` }}
                >
                  <button
                    type="button"
                    onClick={() => openLightbox(idx, c.coverIndex)}
                    className="group block text-left w-full"
                    aria-label={`${t("portfolio.viewImage")} ${c.title}`}
                    data-masonry-content=""
                  >
                    {/* Image with minimal overlay */}
                    <div className="relative">
                      <img
                        src={c.cover.src}
                        alt={c.cover.alt}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto block"
                        onLoad={handleImageLoad}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute left-0 right-0 bottom-0 p-4">
                        <div className="flex items-center gap-2">
                          {count > 1 ? (
                            <span className="inline-flex items-center rounded-full bg-black/60 text-white px-2 py-0.5 text-xs backdrop-blur-sm border border-white/15">
                              +{count - 1} photos
                            </span>
                          ) : null}
                        </div>
                        <h2 className="mt-2 text-lg font-semibold text-white drop-shadow">
                          {c.title}
                        </h2>
                      </div>
                    </div>

                    {/* Chips and summary */}
                    <div className="p-4">
                      <div className="mb-2 flex items-center gap-2 flex-wrap">
                        <ChipsRow category={c.category} tags={c.tags} maxTotal={3} />
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-3">{c.summary}</p>
                    </div>
                  </button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox (Radix Dialog) */}
      <Dialog.Root open={lb.open} onOpenChange={(o) => !o && closeLightbox()}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0"
            style={reduced ? { animation: "none" } : undefined}
          />
        </Dialog.Portal>
        <Dialog.Portal>
          <Dialog.Content
            className="fixed inset-0 flex flex-col"
            style={reduced ? { animation: "none" } : undefined}
            aria-describedby="portfolio-dialog-description"
            aria-labelledby="portfolio-dialog-title"
          >
            <VisuallyHidden>
              <Dialog.Title id="portfolio-dialog-title">{t("portfolio.title")}</Dialog.Title>
              <Dialog.Description id="portfolio-dialog-description">
                {cards[lb.projectIndex]?.title}
              </Dialog.Description>
            </VisuallyHidden>

            {/* Top bar */}
            <div className="pointer-events-none absolute left-0 right-0 top-16 p-4 flex items-center justify-between text-white">
              <div className="pointer-events-auto bg-black/40 rounded-full px-3 py-1 text-xs md:text-sm">
                {cards[lb.projectIndex]?.title}
              </div>
              <div className="flex items-center gap-2">
                <div className="pointer-events-auto bg-black/40 rounded-full px-3 py-1 text-xs md:text-sm">
                  {(lb.imageIndex + 1).toString().padStart(2, "0")} /{" "}
                  {cards[lb.projectIndex]?.images.length ?? 0}
                </div>
                <Dialog.Close asChild>
                  <button
                    aria-label={t("portfolio.closeDialog")}
                    className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/40 hover:bg-black/60"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Image area */}
            <div className="flex-1 flex items-center justify-center px-4">
              {cards[lb.projectIndex] ? (
                <figure className="max-w-[min(92vw,1400px)] max-h-[82vh] flex flex-col items-center">
                  <img
                    key={`${cards[lb.projectIndex].slug}-${lb.imageIndex}`}
                    src={cards[lb.projectIndex].images[lb.imageIndex].src}
                    alt={cards[lb.projectIndex].images[lb.imageIndex].alt}
                    className="max-w-full max-h-[72vh] object-contain select-none"
                    draggable={false}
                    decoding="async"
                  />
                  {cards[lb.projectIndex].images[lb.imageIndex].caption ? (
                    <figcaption className="mt-3 text-center text-xs md:text-sm text-white/90">
                      {cards[lb.projectIndex].images[lb.imageIndex].caption}
                    </figcaption>
                  ) : null}
                </figure>
              ) : null}
            </div>

            {/* Nav buttons */}
            {cards[lb.projectIndex]?.images.length > 1 ? (
              <>
                <button
                  aria-label={t("portfolio.previous")}
                  onClick={() => goto(-1)}
                  className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white"
                >
                  <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" />
                </button>
                <button
                  aria-label={t("portfolio.next")}
                  onClick={() => goto(1)}
                  className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white"
                >
                  <ChevronRight className="h-6 w-6 md:h-7 md:w-7" />
                </button>
              </>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

// ChipsRow component unchanged
function ChipsRow({
  category,
  tags,
  maxTotal = 3,
}: {
  category: string;
  tags: string[];
  maxTotal?: number;
}) {
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]/g, "")
      .trim();

  const alias: Record<string, string> = {
    homeandkitchen: "home",
    kitchen: "home",
    home: "home",
    display: "display",
    vase: "vase",
    statue: "statue",
    decorative: "decorative",
    functional: "functional",
    utility: "functional",
  };

  const displayFor: Record<string, string> = {
    home: "Home",
    display: "Display",
    vase: "Vase",
    statue: "Statue",
    decorative: "Decorative",
    functional: "Functional",
  };

  const PRIORITY: string[] = ["home", "display", "vase", "statue"];

  const toCanonicalKey = (raw: string) => alias[normalize(raw)] ?? normalize(raw);
  const toDisplay = (key: string, fallback: string) => displayFor[key] ?? capitalize(fallback);

  const chips: ReactNode[] = [];
  const seen = new Set<string>();

  const catKey = toCanonicalKey(category);
  if (catKey) {
    chips.push(
      <Badge key={`cat-${catKey}`} variant="outline">
        {toDisplay(catKey, category)}
      </Badge>
    );
    seen.add(catKey);
  }

  const canonicalTags: { key: string; raw: string; label: string }[] = [];
  for (const t of tags) {
    const key = toCanonicalKey(t);
    if (!key) continue;
    if (seen.has(key)) continue;
    canonicalTags.push({ key, raw: t, label: toDisplay(key, t) });
    seen.add(key);
  }

  canonicalTags.sort((a, b) => {
    const ai = PRIORITY.indexOf(a.key);
    const bi = PRIORITY.indexOf(b.key);
    const av = ai === -1 ? Number.POSITIVE_INFINITY : ai;
    const bv = bi === -1 ? Number.POSITIVE_INFINITY : bi;
    if (av !== bv) return av - bv;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });

  const allowedTags = Math.max(0, maxTotal - chips.length);
  for (let i = 0; i < Math.min(allowedTags, canonicalTags.length); i++) {
    const tag = canonicalTags[i];
    chips.push(
      <Badge key={`tag-${tag.key}`} variant="outline">
        {tag.label}
      </Badge>
    );
  }

  return <>{chips}</>;
}

function capitalize(s: string) {
  return s
    .toLowerCase()
    .split(/[\s-_&]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .filter(Boolean)
    .join(" ");
}

export default Portfolio;
// --- END EDIT: Pass content selector, add data-masonry markers, keep a11y, and ensure spans apply ---